import { useEffect, useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../utils/firebase-config";
import { useNavigate } from "react-router-dom";

export default function FreePortfolioCheck() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    investmentType: "",
    goal: "",
    file: null,
    name: "",
    mobile: "",
    email: "",
  });

  const navigate = useNavigate();

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => s - 1);

  // 🔍 Validation helpers
  const isValidMobile = (mobile) => /^[6-9]\d{9}$/.test(mobile);
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.name || form.name.trim().length < 2) {
      newErrors.name = "Please enter your full name";
    }

    if (!form.mobile || !isValidMobile(form.mobile)) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }

    if (form.email && !isValidEmail(form.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      let fileUrl = "";

      if (form.file) {
        const fileRef = ref(
          storage,
          `portfolio_uploads/${Date.now()}_${form.file.name}`
        );
        await uploadBytes(fileRef, form.file);
        fileUrl = await getDownloadURL(fileRef);
      }

      await addDoc(collection(db, "portfolio_requests"), {
        ...form,
        fileUrl,
        status: "new",
        createdAt: serverTimestamp(),
      });

      setStep(5);
    } catch (err) {
      alert("Something went wrong. Please try again.");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (step === 5) {
      const timer = setTimeout(() => {
        navigate("/"); // home page
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [step, navigate]);

  return (
    <div className="bg-black-main-dark text-gray-200">
      <div className="flex justify-center items-center min-h-screen px-4">
        <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-8">
          {/* Progress */}
          {step <= 4 && (
            <div className="mb-6">
              <div className="h-1 bg-zinc-800 rounded">
                <div
                  className="h-1 bg-green-500 rounded transition-all"
                  style={{ width: `${(step - 1) * 25}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Step {step} of 4</p>
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <h1 className="text-xl font-semibold text-white mb-4">
                What are you currently investing in?
              </h1>

              {["Mutual Funds", "Stocks", "Both"].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    setForm({ ...form, investmentType: item });
                    next();
                  }}
                  className="w-full mb-3 py-3 rounded-lg border border-zinc-700 hover:border-green-500 transition"
                >
                  {item}
                </button>
              ))}
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <h1 className="text-xl font-semibold text-white mb-4">
                What do you want help with?
              </h1>

              {[
                "Check mistakes in my portfolio",
                "Reduce risk",
                "Plan long-term wealth",
              ].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    setForm({ ...form, goal: item });
                    next();
                  }}
                  className="w-full mb-3 py-3 rounded-lg border border-zinc-700 hover:border-green-500 transition"
                >
                  {item}
                </button>
              ))}

              <button onClick={back} className="text-xs text-gray-500 mt-4">
                ← Back
              </button>
            </>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <>
              <h1 className="text-xl font-semibold text-white mb-4">
                Upload your portfolio (optional)
              </h1>

              <input
                type="file"
                onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                className="text-sm text-gray-400 mb-6"
              />

              <button
                onClick={next}
                className="w-full bg-green-600 text-black py-3 rounded-lg font-medium"
              >
                Continue
              </button>

              <button onClick={back} className="text-xs text-gray-500 mt-4">
                ← Back
              </button>
            </>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <>
              <h1 className="text-xl font-semibold text-white mb-4">
                Where should we contact you?
              </h1>

              <input
                placeholder="Your Name"
                className="w-full mb-1 bg-black border border-zinc-700 rounded-lg px-4 py-3"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              {errors.name && (
                <p className="text-xs text-red-400 mb-2">{errors.name}</p>
              )}

              <input
                placeholder="Mobile Number"
                className="w-full mb-1 bg-black border border-zinc-700 rounded-lg px-4 py-3"
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              />
              {errors.mobile && (
                <p className="text-xs text-red-400 mb-2">{errors.mobile}</p>
              )}

              <input
                placeholder="Email (optional)"
                className="w-full mb-1 bg-black border border-zinc-700 rounded-lg px-4 py-3"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              {errors.email && (
                <p className="text-xs text-red-400 mb-2">{errors.email}</p>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`w-full py-3 rounded-lg font-medium ${
                  loading
                    ? "bg-zinc-600 text-gray-300"
                    : "bg-green-600 text-black hover:bg-green-700"
                }`}
              >
                {loading ? "Submitting..." : "Request Free Portfolio Check"}
              </button>

              <p className="text-xs text-gray-500 mt-4">
                No tips. No selling. You decide the next step.
              </p>
            </>
          )}

          {/* SUCCESS */}
          {step === 5 && (
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-white mb-2">
                Thank you!!
              </h1>
              <p className="text-gray-400 text-md">
                We’ll review your portfolio and contact you within 24 hours.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Redirecting to home...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
